/**
 * API Client for Filmssavn
 * Communicates with the Flowcore Video API
 */

import { getToken, logout, refreshAccessToken } from './keycloak.js';

// Auto-detect API URL based on current host
// Uses relative URL if VITE_API_URL is not set, making it work from any domain/IP
const getApiUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  // Use relative URL - nginx will proxy /api to the backend
  return '/api';
};

const API_URL = getApiUrl();
const _TENANT_ID = import.meta.env.VITE_FLOWCORE_TENANT || '';

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
    this.token = null;
  }

  /**
   * Resolve an asset URL returned by the API into one the browser can load.
   * The API stores image URLs as path-relative `/images/asset/{id}` strings,
   * which resolve against the page origin (stroyma.fo) where there is no
   * such route. Prepend the API base so the request reaches the API.
   * URLs that are already absolute (http(s)://, data:, blob:) pass through.
   */
  resolveAsset(url) {
    if (!url) return null;
    if (/^(https?:|data:|blob:)/i.test(url)) return url;
    return `${this.baseUrl}${url}`;
  }

  isAssetProxyUrl(url) {
    const resolved = this.resolveAsset(url);
    return !!resolved && /\/images\/asset\/[^/?#]+/.test(resolved);
  }

  resolveImageAsset(url, options = {}, config = {}) {
    const resolved = this.resolveAsset(url);
    if (!resolved || !this.isAssetProxyUrl(resolved)) return resolved;

    const [withoutHash, hash = ''] = resolved.split('#');
    const [base, query = ''] = withoutHash.split('?');
    const params = new URLSearchParams(query);

    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && (config.override || !params.has(key))) {
        params.set(key, String(value));
      }
    });

    const nextQuery = params.toString();
    return `${base}${nextQuery ? `?${nextQuery}` : ''}${hash ? `#${hash}` : ''}`;
  }

  /**
   * Set authentication token
   */
  setToken(token) {
    this.token = token;
  }

  /**
   * Make a request to the API
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    // Get fresh token (with automatic refresh if needed)
    // Don't fail if token retrieval fails - some endpoints don't require auth
    let token = null;
    try {
      token = await getToken();
    } catch (error) {
      console.warn('[API] Failed to get token (may be OK for public endpoints):', error);
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle 401 Unauthorized - try to refresh token and retry once
      if (response.status === 401) {
        console.warn('[API] Received 401, attempting token refresh...');
        const refreshed = await refreshAccessToken();

        if (refreshed) {
          // Retry request with new token
          const newToken = await getToken();
          headers.Authorization = `Bearer ${newToken}`;

          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });

          if (!retryResponse.ok) {
            if (retryResponse.status === 401) {
              // Still 401 after refresh, session truly expired
              console.error('[API] Session expired after refresh, logging out...');
              this.handleSessionExpired();
            }
            throw new Error(`API Error: ${retryResponse.status} ${retryResponse.statusText}`);
          }

          return retryResponse.json();
        } else {
          // Refresh failed, session expired
          console.error('[API] Token refresh failed, session expired');
          this.handleSessionExpired();
          throw new Error('Session expired, please log in again');
        }
      }

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      // Check if it's a network error or session expiration
      if (error.message.includes('Session expired')) {
        throw error;
      }
      throw error;
    }
  }

  /**
   * Handle session expiration
   */
  handleSessionExpired() {
    // Show user-friendly message
    const message = document.createElement('div');
    message.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #F5A623;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 600;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    `;
    message.textContent = 'Session expired. Redirecting to login...';
    document.body.appendChild(message);

    // Logout after 2 seconds
    setTimeout(() => {
      logout();
    }, 2000);
  }

  /**
   * Get all videos
   */
  async getVideos() {
    try {
      const result = await this.request('/videos');
      console.log('[API] getVideos response:', result);
      return result;
    } catch (error) {
      console.error('[API] Failed to fetch videos:', error);
      console.error('[API] Error details:', {
        message: error.message,
        stack: error.stack,
      });
      return { videos: [] };
    }
  }

  /**
   * Get a single video by ID
   */
  async getVideo(videoId) {
    try {
      return await this.request(`/videos/${videoId}`);
    } catch (error) {
      console.error('Failed to fetch video:', error);
      throw error;
    }
  }

  /**
   * Get video stream URL
   */
  getStreamUrl(videoId) {
    return `${this.baseUrl}/stream/${videoId}/manifest.m3u8`;
  }

  /**
   * Upload video
   */
  async uploadVideo(file, metadata = {}) {
    const formData = new FormData();
    formData.append('video', file);

    if (metadata.title) formData.append('title', metadata.title);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.isFaroese) formData.append('isFaroese', 'true');

    return await this.request('/videos/upload', {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }

  /**
   * Get video metadata
   */
  async getVideoMetadata(videoId) {
    try {
      return await this.request(`/videos/${videoId}/metadata`);
    } catch (error) {
      console.error('Failed to fetch video metadata:', error);
      return null;
    }
  }

  /**
   * Create rental checkout session
   */
  async createRentalCheckout(videoId, attemptId) {
    try {
      return await this.request('/rentals/create-checkout', {
        method: 'POST',
        body: JSON.stringify({ videoId, ...(attemptId ? { attemptId } : {}) }),
      });
    } catch (error) {
      console.error('Failed to create checkout:', error);
      throw error;
    }
  }

  /**
   * Reconcile a rental checkout with its authenticated server-side attempt.
   */
  async reconcileRentalPayment(attemptId, videoId, options = {}) {
    return await this.request('/rentals/payment-status', {
      method: 'POST',
      body: JSON.stringify({ attemptId, videoId }),
      signal: options.signal,
    });
  }

  /**
   * Check if user has access to a video
   */
  async checkVideoAccess(videoId, { throwOnError = false, signal } = {}) {
    try {
      return await this.request(`/rentals/check-access/${videoId}`, { signal });
    } catch (error) {
      console.error('Failed to check video access:', error);
      if (throwOnError) throw error;
      return { hasAccess: false };
    }
  }

  /**
   * Get user's rental history
   */
  async getMyRentals() {
    try {
      return await this.request('/rentals/my-rentals');
    } catch (error) {
      console.error('Failed to fetch rentals:', error);
      return { rentals: [] };
    }
  }

  /**
   * Get videos user has rented
   */
  async getMyVideos() {
    try {
      return await this.request('/rentals/my-videos');
    } catch (error) {
      console.error('Failed to fetch accessible videos:', error);
      return { videos: [] };
    }
  }

  /**
   * Save watch progress for resume playback
   */
  async saveWatchProgress(videoId, positionSeconds, durationSeconds, completed = false) {
    try {
      return await this.request(`/analytics/watch-progress/${videoId}`, {
        method: 'POST',
        body: JSON.stringify({ positionSeconds, durationSeconds, completed }),
      });
    } catch (error) {
      console.error('Failed to save watch progress:', error);
      return { success: false };
    }
  }

  /**
   * Get watch progress for a video
   */
  async getWatchProgress(videoId) {
    try {
      return await this.request(`/analytics/watch-progress/${videoId}`);
    } catch (error) {
      console.error('Failed to get watch progress:', error);
      return { progress: null };
    }
  }

  /**
   * Get continue watching list
   */
  async getContinueWatching() {
    try {
      return await this.request('/analytics/continue-watching');
    } catch (error) {
      console.error('Failed to get continue watching:', error);
      return { videos: [] };
    }
  }

  /**
   * Record analytics event
   */
  async recordAnalyticsEvent(videoId, eventType, data = {}) {
    try {
      return await this.request('/analytics/event', {
        method: 'POST',
        body: JSON.stringify({ videoId, eventType, ...data }),
      });
    } catch (error) {
      console.error('Failed to record analytics event:', error);
      return { success: false };
    }
  }

  /**
   * Get video analytics
   */
  async getVideoAnalytics(videoId) {
    try {
      return await this.request(`/analytics/video/${videoId}`);
    } catch (error) {
      console.error('Failed to get video analytics:', error);
      return { analytics: null };
    }
  }
}

export const api = new ApiClient();
